import { parseProjectConfig } from '@viko/project-config'
import { Button, Sheet } from '@viko/transcript-ui'
import { useState } from 'react'
import { useBackend } from '../lib/backend.js'
import type { Project } from '../lib/types.js'

export function ProjectConfigSheet({
  project,
  onClose,
  onSaved,
}: {
  project?: Project
  onClose(): void
  onSaved(project: Project): Promise<void> | void
}) {
  const backend = useBackend()
  const [yaml, setYaml] = useState(project?.yaml ?? '')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const save = async () => {
    setError('')
    if (new TextEncoder().encode(yaml).byteLength > 128_000)
      return setError('Configuration must be smaller than 128 KB.')
    const parsed = parseProjectConfig(yaml)
    if (!parsed.ok)
      return setError(
        parsed.errors.map((e) => `${e.path || 'Configuration'}: ${e.message}`).join('\n'),
      )
    setBusy(true)
    try {
      const saved = project
        ? await backend.api.updateProject(project.id, { yaml })
        : await backend.api.createProject({ yaml })
      await onSaved(saved)
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save project configuration')
    } finally {
      setBusy(false)
    }
  }
  const choose = async (file: File | undefined) => {
    if (!file) return
    if (file.size > 128_000) return setError('Configuration must be smaller than 128 KB.')
    try {
      setYaml(await file.text())
      setError('')
    } catch {
      setError('Could not read this configuration file')
    }
  }
  return (
    <Sheet
      open
      onClose={onClose}
      title={project ? 'Edit configuration' : 'Add project'}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy || !yaml.trim()} onClick={() => void save()}>
            {project ? 'Save configuration' : 'Import project'}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 pb-2">
        <p className="m-0 text-[14px] text-text-3">
          Use the viko.project.yaml from a repository already added to your machine. Its local
          configuration continues to control execution and landing.
        </p>
        <label className="field">
          <span>Choose configuration file</span>
          <input
            type="file"
            accept=".yaml,.yml"
            disabled={busy}
            onChange={(e) => void choose(e.target.files?.[0])}
          />
        </label>
        <label className="field">
          <span>Project configuration</span>
          <textarea
            className="input font-mono"
            rows={14}
            value={yaml}
            disabled={busy}
            spellCheck={false}
            onChange={(e) => setYaml(e.target.value)}
          />
        </label>
        {error ? (
          <p role="alert" className="m-0 whitespace-pre-wrap text-[14px] text-status-fail">
            {error}
          </p>
        ) : null}
      </div>
    </Sheet>
  )
}
